import type { Schemas } from '@seoai/api-types';
import { sessionStore } from '../auth/session';
import { currentLocale } from '../i18n/locale';
import { ApiError, NETWORK_ERROR_CODE, type Envelope } from './envelope';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * TEK API giriş noktası (SEOAI-78). Her istek buradan geçer; başlıklar, zarf
 * açma ve oturum yenileme tek yerde durur.
 */
type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Yenileme çağrısının kendisi tekrar yenilemeye girmemeli (sonsuz döngü). */
  skipRefresh?: boolean;
};

/**
 * EŞZAMANLI 401'LERDE TEK YENİLEME.
 *
 * Backend refresh token'ı ROTASYONLA değiştirir (SEOAI-16): ikinci bir yenileme
 * çağrısı, birincinin verdiği token'ı geçersiz kılar. Sayfa açılışında paralel
 * koşan üç sorgu aynı anda 401 alırsa, korumasız bir istemci üç yenileme başlatır
 * ve kullanıcı sebepsiz yere oturumdan atılır. Bu yüzden ilk 401 yenilemeyi
 * başlatır, diğerleri AYNI sözü bekler.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const tokens = sessionStore.tokens();
  if (!tokens) return false;

  try {
    const refreshed = await request<Schemas['TokenPairDTO']>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken: tokens.refreshToken },
      skipRefresh: true,
    });

    /*
     * Yanıtın TAMAMI uygulanır (SEOAI-80): jetonlar, kurum ve "başka kurum var
     * mı" bilgisi. rotate() kurumu korur, yani değerler normalde aynıdır; yine
     * de sunucudan yazmak doğru — tek kaynak sunucudur. Çokluk bayrağı burada
     * TAZELENİR: kullanıcı oturumdayken ikinci bir kuruma davet edilirse kurum
     * değiştirici yeniden giriş beklemeden belirir.
     */
    sessionStore.applySession(refreshed);

    return true;
  } catch {
    sessionStore.clear();

    return false;
  }
}

function ensureRefresh(): Promise<boolean> {
  refreshInFlight ??= refreshSession().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * Sayfalı yanıt: gövde + zarf meta'sı (SEOAI-81). `request` yalnızca `data`
 * döndürür; sayfalama bilgisi meta'dadır ve onu atmak, listenin eksik olduğunu
 * kullanıcıdan gizlerdi.
 */
export type Paginated<TItem> = {
  items: TItem[];
  page: number;
  perPage: number;
  total: number;
  lastPage: number;
};

export async function requestPaginated<TItem>(
  path: string,
  options: RequestOptions = {},
): Promise<Paginated<TItem>> {
  const envelope = await requestEnvelope<TItem[]>(path, options);
  // Şema ÜRETİLİR (SEOAI-42): sayfalama alanları PaginatedEnvelopeMeta'da tanımlı.
  const meta = envelope.meta as Schemas['PaginatedEnvelopeMeta'] | undefined;

  return {
    items: envelope.data ?? [],
    page: meta?.page ?? 1,
    perPage: meta?.perPage ?? 0,
    total: meta?.total ?? 0,
    lastPage: meta?.lastPage ?? 1,
  };
}

export async function request<TData>(path: string, options: RequestOptions = {}): Promise<TData> {
  const response = await send(path, options);

  // 401 → bir kez yenile ve isteği TEKRARLA. Yenileme düşerse oturum kapanır.
  if (response.status === 401 && !options.skipRefresh && sessionStore.tokens()) {
    const ok = await ensureRefresh();

    if (ok) {
      return unwrap<TData>(await send(path, options));
    }
  }

  return unwrap<TData>(response);
}

/** `request` ile aynı akış; farkı, zarfın TAMAMINI döndürmesi (meta için). */
async function requestEnvelope<TData>(path: string, options: RequestOptions): Promise<Envelope<TData>> {
  const response = await send(path, options);

  if (response.status === 401 && !options.skipRefresh && sessionStore.tokens()) {
    if (await ensureRefresh()) {
      return envelopeOf<TData>(await send(path, options));
    }
  }

  return envelopeOf<TData>(response);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const tokens = sessionStore.tokens();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Çözülen dil her istekte bildirilir (SEOAI-65); yanıt meta.locale ile teyit eder.
    'X-Locale': currentLocale(),
  };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;

  try {
    return await fetch(`${BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch (cause) {
    throw new ApiError(
      NETWORK_ERROR_CODE,
      cause instanceof Error ? cause.message : 'Ağ hatası',
      0,
    );
  }
}

async function unwrap<TData>(response: Response): Promise<TData> {
  return (await envelopeOf<TData>(response)).data as TData;
}

/** Zarfı açar ve hata durumunda ApiError fırlatır. */
async function envelopeOf<TData>(response: Response): Promise<Envelope<TData>> {
  // Korelasyon kimliği: hata ekranında gösterilir, sunucudaki log kaydıyla eşleşir.
  const requestId = response.headers.get('X-Request-Id');

  if (response.status === 204) return { success: true, data: undefined as TData, message: null, meta: { locale: '' } };

  let envelope: Envelope<TData>;

  try {
    envelope = (await response.json()) as Envelope<TData>;
  } catch {
    throw new ApiError(NETWORK_ERROR_CODE, 'Yanıt okunamadı.', response.status, null, requestId);
  }

  if (!response.ok || envelope.success === false) {
    const error = envelope.error;

    throw new ApiError(
      error?.code ?? 'CLIENT.RESPONSE.UNKNOWN',
      error?.message ?? 'Beklenmeyen bir hata oluştu.',
      response.status,
      error?.details ?? null,
      requestId,
    );
  }

  return envelope;
}
